/**
 * Comment API
 * Handles all API communication and CRUD operations for comments
 * Extracted from comment-edit-controller.js, comment-menu-controller.js, comment-form-controller.js
 */

import { ApiBase } from "../core/api-base";
import { notifier } from "../core/notifier/notifier";
import { z } from "zod";
import {
    AddCommentToPost201Response,
    AddCommentToPost400Response,
    AddCommentToPost403Response,
    AddCommentToPost404Response,
    AddCommentToPost422Response,
    AddCommentToPostBody,
    DeleteComment200Response,
    DeleteComment400Response,
    DeleteComment403Response,
    DeleteComment404Response,
    DeleteComment500Response,
    GetCommentsForPost200Response,
    UpdateComment200Response,
    UpdateComment400Response,
    UpdateComment403Response,
    UpdateComment404Response,
    UpdateComment422Response,
    UpdateComment500Response,
    UpdateCommentBody,
} from "../api/generated/posts.zod";
import { CommentUtils } from "./comment-utils";
import type { CommentItemId } from "./comment-render";

type CommentCollectionResponse = z.infer<typeof GetCommentsForPost200Response>;
type CommentMutationResponse = z.infer<typeof AddCommentToPost201Response>;
type CommentUpdateResponse = z.infer<typeof UpdateComment200Response>;
type CommentDeleteResponse = z.infer<typeof DeleteComment200Response>;
type CommentValidationErrorResponse =
    | z.infer<typeof AddCommentToPost422Response>
    | z.infer<typeof UpdateComment422Response>;
type CommentErrorResponse =
    | z.infer<typeof AddCommentToPost400Response>
    | z.infer<typeof AddCommentToPost403Response>
    | z.infer<typeof AddCommentToPost404Response>
    | z.infer<typeof UpdateComment400Response>
    | z.infer<typeof UpdateComment403Response>
    | z.infer<typeof UpdateComment404Response>
    | z.infer<typeof UpdateComment500Response>
    | z.infer<typeof DeleteComment400Response>
    | z.infer<typeof DeleteComment403Response>
    | z.infer<typeof DeleteComment404Response>
    | z.infer<typeof DeleteComment500Response>;

interface BoardAndPostIds {
    boardId: string;
    postId: number | null;
}

class CommentApiService extends ApiBase {
    public static async parseCommentErrorResponse(response: Response): Promise<CommentErrorResponse> {
        const payload = (await response.json().catch(() => ({}))) as unknown;
        const addCommentErrorSchema = z.union([
            AddCommentToPost400Response,
            AddCommentToPost403Response,
            AddCommentToPost404Response,
        ]);
        const updateCommentErrorSchema = z.union([
            UpdateComment400Response,
            UpdateComment403Response,
            UpdateComment404Response,
            UpdateComment500Response,
        ]);
        const deleteCommentErrorSchema = z.union([
            DeleteComment400Response,
            DeleteComment403Response,
            DeleteComment404Response,
            DeleteComment500Response,
        ]);

        for (const schema of [addCommentErrorSchema, updateCommentErrorSchema, deleteCommentErrorSchema]) {
            const parsed = schema.safeParse(payload);
            if (parsed.success) {
                return parsed.data;
            }
        }

        return {
            message: undefined,
            errors: undefined,
            status_code: undefined,
            success: undefined,
        };
    }

    public static buildCommentErrorMessage(
        errorData: CommentValidationErrorResponse | CommentErrorResponse,
        fallbackMessage: string,
    ): string {
        if (typeof errorData.message === "string" && errorData.message.trim()) {
            const normalizedErrors = errorData.errors;
            if (normalizedErrors && typeof normalizedErrors === "object") {
                const fieldErrors = Object.values(normalizedErrors)
                    .flatMap((value) => (Array.isArray(value) ? value : [value]))
                    .map((value) => String(value))
                    .filter(Boolean);
                if (fieldErrors.length > 0 && errorData.message.toLowerCase() === "validation failed") {
                    return fieldErrors.join(", ");
                }
            }

            return errorData.message;
        }

        if (errorData.errors && typeof errorData.errors === "object") {
            const fieldErrors = Object.values(errorData.errors)
                .flatMap((value) => (Array.isArray(value) ? value : [value]))
                .map((value) => String(value))
                .filter(Boolean);
            if (fieldErrors.length > 0) {
                return fieldErrors.join(", ");
            }
        }

        return fallbackMessage;
    }

    /**
     * Fetch all comments for a post
     */
    public static async fetchComments(boardId: string, postId: number): Promise<CommentCollectionResponse> {
        const url = `/api/v1/posts/${boardId}/${postId}/comments`;
        const response = this.interceptAuthErrors(await super.apiGet(url));

        if (response.ok) {
            return (await response.json()) as CommentCollectionResponse;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    /**
     * Fetch replies for a specific comment
     */
    public static async fetchReplies(
        boardId: string,
        postId: number,
        commentId: number,
    ): Promise<CommentCollectionResponse> {
        const url = `/api/v1/posts/${boardId}/${postId}/comments?parent_id=${commentId}`;
        const response = this.interceptAuthErrors(await super.apiGet(url));

        if (response.ok) {
            return (await response.json()) as CommentCollectionResponse;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    /**
     * Create new comment
     */
    public static async createComment(
        boardId: string,
        postId: number,
        content: z.infer<typeof AddCommentToPostBody>["content"],
        parentId: CommentItemId | null = null,
    ): Promise<CommentMutationResponse> {
        const payload: z.infer<typeof AddCommentToPostBody> = { content };

        if (parentId) {
            payload.parent_comment_id = Number(parentId);
        }

        const url = `/api/v1/posts/${boardId}/${postId}/comments`;
        const response = this.interceptAuthErrors(await super.apiPost(url, payload));

        if (response.ok) {
            const data = (await response.json()) as CommentMutationResponse;
            if (data.success === 1) {
                return data;
            }
            throw new Error("Creation failed");
        }
        if (response.status === 422) {
            const errorData = (await response.json().catch(() => ({}))) as CommentValidationErrorResponse;
            const errorMessage = this.buildCommentErrorMessage(errorData, "Validation failed");
            throw new Error(errorMessage);
        }
        const errorData = await this.parseCommentErrorResponse(response);
        throw new Error(
            this.buildCommentErrorMessage(errorData, `HTTP ${response.status}: ${response.statusText}`),
        );
    }

    /**
     * Update existing comment
     */
    public static async updateComment(
        boardId: string,
        postId: number,
        commentId: number,
        content: z.infer<typeof UpdateCommentBody>["content"],
    ): Promise<CommentUpdateResponse> {
        const url = `/api/v1/posts/${boardId}/${postId}/comments/${commentId}`;
        const requestData: z.infer<typeof UpdateCommentBody> = { content };
        const response = this.interceptAuthErrors(await super.apiPut(url, requestData));

        if (response.ok) {
            const data = (await response.json()) as CommentUpdateResponse;
            if (data.success === 1) {
                return data;
            }
            throw new Error("Update failed");
        }
        await notifier.fromResponse(response, "comments_edit_error");
        const errorData = await this.parseCommentErrorResponse(response);
        throw new Error(
            this.buildCommentErrorMessage(errorData, `HTTP ${response.status}: ${response.statusText}`),
        );
    }

    /**
     * Delete comment
     */
    public static async deleteComment(
        boardId: string,
        postId: number,
        commentId: number,
    ): Promise<CommentDeleteResponse> {
        const url = `/api/v1/posts/${boardId}/${postId}/comments/${commentId}`;
        const response = this.interceptAuthErrors(await super.apiDelete(url));

        if (response.ok) {
            const data = (await response.json()) as CommentDeleteResponse;
            if (data.success === 1) {
                return data;
            }
            throw new Error("Delete failed");
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    // ========================================
    // URL Utility Methods
    // ========================================

    /**
     * Get board and post IDs from context
     */
    public static getBoardAndPostIds(): BoardAndPostIds {
        return CommentUtils.getBoardAndPostIds();
    }

    public static getBoardIdFromUrl(): string {
        return CommentUtils.getBoardIdFromUrl();
    }

    public static getPostIdFromUrl(): number | null {
        return CommentUtils.getPostIdFromUrl();
    }
}

export { CommentApiService };
